import React from "react";

import { useCurrentFile } from "@agent-ui/features/page-renderer/hooks/useCurrentFile";
import {
  useEditorStoreActions,
  usePanelView,
} from "@agent-ui/store/editorStore";
import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { nextJSProjectRouter, nextJSProjectUtils } from "@trpc/nextJSProject";
import { useAdminPanelActions } from "@v2/features/admin/adminPanelStore";
import { DesignLibraryIcon } from "@v2/features/design-library/designLibraryIcon";
import { DeletePageModal } from "@v2/features/page-renderer/components/navbar/DeletePageModal";
import { UnpublishModal } from "@v2/features/page-renderer/components/navbar/UnpublishModal";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { Bug, Copy, EyeOff, Settings, Settings2, Trash2 } from "lucide-react";

const PageSettingsMenu = () => {
  const { open: openDebugPanel } = useAdminPanelActions();
  const isDebugMode = useIsDebugMode();
  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();
  const currentFile = useCurrentFile();
  const [unpublishModalOpen, setUnpublishModalOpen] = React.useState(false);
  const [deletePageModalOpen, setDeletePageModalOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const projectId = useCurrentProjectId();

  const isPagePublished = Boolean(currentFile?.lastPublishedFileVersionId);

  const { mutate: duplicatePage, isPending: isDuplicating } =
    nextJSProjectRouter.duplicatePage.useMutation({
      onSuccess: async ({ createdFile }) => {
        void nextJSProjectUtils.listPages.invalidate({ projectId });
        toast.success({
          title: "Page duplicated",
          description: `"${createdFile.name}" was created`,
        });
      },
      onError: () => {
        toast.error({
          title: "Failed to duplicate page",
          description: "Please try again or contact support@replo.app.",
        });
      },
    });

  return (
    <>
      <Menu>
        <MenuTrigger
          render={
            <Button
              icon
              variant="tertiary"
              size="sm"
              UNSAFE_className={twMerge(
                "rounded-full",
                (panelView === "designLibrary" ||
                  panelView === "pageSettings") &&
                  "bg-sky-soft text-sky border-sky-border hover:bg-sky-soft hover:text-sky hover:border-sky-border",
              )}
            >
              <Settings2
                size={14}
                className={twMerge(
                  (panelView === "designLibrary" ||
                    panelView === "pageSettings") &&
                    "text-primary",
                )}
              />
            </Button>
          }
        />
        <MenuContent align="start" side="bottom" UNSAFE_className="w-[166px]">
          <MenuItem
            label="Page Settings"
            startEnhancer={<Settings size={12} />}
            selected={panelView === "pageSettings"}
            onClick={() => setPanelView("pageSettings")}
          />
          <MenuItem
            label="Design Library"
            startEnhancer={<DesignLibraryIcon size={12} />}
            selected={panelView === "designLibrary"}
            onClick={() => setPanelView("designLibrary")}
          />
          <MenuItem
            label="Duplicate Page"
            startEnhancer={<Copy size={12} />}
            disabled={isDuplicating}
            onClick={() => {
              if (projectId && currentFile) {
                duplicatePage({
                  projectId,
                  fileId: currentFile.id,
                });
              }
            }}
          />
          {isMobile && isDebugMode && (
            <MenuItem
              label="Open Debug Panel"
              startEnhancer={<Bug size={12} />}
              onClick={() => openDebugPanel()}
            />
          )}
          {isPagePublished && (
            <MenuItem
              label="Unpublish Page"
              startEnhancer={<EyeOff size={12} />}
              onClick={() => {
                if (currentFile) {
                  setUnpublishModalOpen(true);
                }
              }}
            />
          )}
          <MenuSeparator />
          <MenuItem
            label="Delete Page"
            startEnhancer={<Trash2 size={12} />}
            variant="destructive"
            disabled={isPagePublished}
            onClick={() => {
              if (currentFile) {
                setDeletePageModalOpen(true);
              }
            }}
          />
        </MenuContent>
      </Menu>
      {currentFile && (
        <UnpublishModal
          open={unpublishModalOpen}
          onOpenChange={setUnpublishModalOpen}
          type="page"
          page={currentFile}
        />
      )}
      {currentFile && (
        <DeletePageModal
          open={deletePageModalOpen}
          onOpenChange={setDeletePageModalOpen}
          page={currentFile}
        />
      )}
    </>
  );
};

export default PageSettingsMenu;
